import { useGetAuthData } from "@/lib/hooks"
import { Link, useNavigate, useMatches } from "@tanstack/react-router"
import {
  ArrowRightStartOnRectangleIcon,
  CreditCardIcon,
  InformationCircleIcon,
  LinkIcon,
} from "@heroicons/react/16/solid"
import React, { useEffect, useState } from "react"
import { PillarsBetaLogo } from "./pillars-beta-logo"
import { cn } from "@/lib/utils"
import { logout } from "@/lib/firebase"
import { Button } from "./ui/button"
import { ExternalLink } from "lucide-react"
import { usePermStore } from "@/lib/perm-store.js"
import { client, validateClientProperty } from "@/lib/ts-rest"
import Fuse from "fuse.js"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Input } from "./ui/input"
import { isEmailPrivileged } from "../../../api/src/lib/utilsWithoutImports.js"
import { queryClient } from "@/lib/react-query.js"
import { trackEvent } from "@/lib/log"

export function NavMenu({
  showLogo = true,
  onClose,
}: {
  showLogo?: boolean
  onClose?: () => void
}) {
  const { data } = useGetAuthData()
  const navigate = useNavigate()
  const matches = useMatches()
  const { emulateUserId, setPermStore } = usePermStore()
  const [showEmulateModal, setShowEmulateModal] = useState(false)
  const { refetch: fetchPortalLink } = client.getStripePortalLink.useQuery({
    queryKey: [validateClientProperty("getStripePortalLink")],
    enabled: false,
  })

  if (!data) return
  const {
    body: {
      meta: { pictureUrl, authEmail },
      user,
    },
  } = data

  function openFaqs() {
    trackEvent("opened FAQs")
    const url = "https://www.thepillarsapp.com/speaker#footer"
    window.open(url, "_blank")
  }

  function startEmulateUser(emulateUserId: string | undefined) {
    queryClient.resetQueries()
    setPermStore({ emulateUserId })
    setShowEmulateModal(false)
  }

  const tabs: {
    label: string
    to?: NonNullable<Parameters<typeof navigate>[0]>["from"]
    onClick?: () => void
    Icon: React.ElementType
  }[] = [
    {
      label: "Connections",
      to: "/",
      Icon: LinkIcon,
    },
    {
      label: "FAQs",
      onClick: () => openFaqs(),
      Icon: InformationCircleIcon,
    },
    // {
    //   label: "Settings",
    //   to: "/settings",
    //   Icon: Cog6ToothIcon,
    //   active: Boolean(
    //     matches.find((item) => item.id === "/dashboard/settings")
    //   ),
    // },
  ]

  if (user?.hasSpeakerSubscription && user?.stripeCustomerId) {
    tabs.push({
      label: "Billing",
      onClick: async () => {
        trackEvent("opened billing from nav")
        const { data } = await fetchPortalLink()
        if (data?.body.url) {
          window.open(data.body.url, "_blank")
        }
      },
      Icon: CreditCardIcon,
    })
  }

  return (
    <>
      <div className="flex flex-col justify-between gap-10 h-full">
        <div>
          {showLogo && (
            <>
              <PillarsBetaLogo />

              <div className="border-b my-10"></div>
            </>
          )}
          <div className="flex flex-col gap-5">
            {tabs.map(({ label, to, Icon, onClick }) => (
              <Link
                key={label}
                to={to}
                onClick={() => {
                  onClose?.()
                  onClick?.()
                }}
                className={cn(
                  "flex gap-3 items-center",
                  matches.find((item) => item.id === `/dashboard${to}`)
                    ? "text-primary font-medium"
                    : "text-primary/40"
                )}
              >
                <Icon className="h-5 w-5 mb-[2px]" />
                {label}
                {onClick && <ExternalLink className="h-auto w-4 mb-[2px]" />}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex flex-col overflow-hidden">
          {pictureUrl && (
            <img
              src={pictureUrl}
              className={cn(
                "w-10 h-10 aspect-square shrink-0 rounded-full bg-center bg-contain bg-no-repeat bg-gray-100 mb-1",
                isEmailPrivileged(authEmail) && "cursor-pointer",
                emulateUserId && "ring-2 ring-red-500"
              )}
              referrerPolicy="no-referrer"
              onClick={
                isEmailPrivileged(authEmail)
                  ? () => {
                      if (emulateUserId) {
                        startEmulateUser(undefined)
                      } else {
                        setShowEmulateModal(true)
                      }
                    }
                  : undefined
              }
            />
          )}
          <div>
            <div className="text-sm">
              {user.firstName} {user.lastName}
            </div>
            <div
              className="text-sm text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap"
              title={user.email ?? ""}
            >
              {user.email}
            </div>
            <Button
              onClick={() => {
                trackEvent("signed out")
                onClose?.()
                logout()
              }}
              className="mt-2"
              variant="outline"
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>

      <EmulateDialog
        open={showEmulateModal}
        onOpenChange={setShowEmulateModal}
        startEmulateUser={startEmulateUser}
      />
    </>
  )
}

function EmulateDialog({
  open,
  onOpenChange,
  startEmulateUser,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  startEmulateUser: (emulateUserId: string | undefined) => void
}) {
  const { emulateUserId } = usePermStore()
  const { data: usersResponse, refetch } = client.getAllUsers.useQuery({
    queryKey: [validateClientProperty("getAllUsers")],
    enabled: false,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (open) {
      refetch()
    }
  }, [open, refetch])

  const [emulateUserSearchText, setEmulateUserSearchText] = useState<string>("")

  let allUsers = (usersResponse?.body.users ?? []).map((item) => {
    return {
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      hasSpeakerSubscription: item.hasSpeakerSubscription,
    }
  })

  type KeyArray = (keyof (typeof allUsers)[number])[]
  const limit = 20
  if (emulateUserSearchText) {
    const fuse = new Fuse(allUsers, {
      keys: ["firstName", "lastName", "email"] satisfies KeyArray,
    })
    allUsers = fuse
      .search(emulateUserSearchText, { limit })
      .map((item) => item.item)
  } else {
    allUsers = allUsers.slice(0, limit)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => onOpenChange(open)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Emulate user</DialogTitle>
          <DialogDescription>
            Search for a user to emulate. This functionality is only available
            for users with an @thepillarsapp.com email.
          </DialogDescription>
        </DialogHeader>
        <div>
          {emulateUserId ? (
            <Button
              onClick={() => startEmulateUser(undefined)}
              variant="destructive"
            >
              Reset
            </Button>
          ) : (
            <div>
              <div className="mb-3">
                <Input
                  className="w-full focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={emulateUserSearchText}
                  onChange={(event) =>
                    setEmulateUserSearchText(event.target.value)
                  }
                  autoFocus
                  placeholder="Search"
                />
              </div>
              <div className="flex flex-col rounded-xl border h-[50vh] overflow-auto text-sm">
                {allUsers.map(
                  (
                    { id, firstName, lastName, email, hasSpeakerSubscription },
                    index
                  ) => (
                    <div
                      onClick={() => startEmulateUser(id)}
                      key={id}
                      className={cn(
                        "p-3 hover:bg-gray-100 cursor-pointer flex flex-col gap-2",
                        index !== 0 && "border-t"
                      )}
                    >
                      <div className="flex gap-1">
                        <div>
                          {firstName} {lastName}
                        </div>
                        <span className="text-gray-500">/ {email}</span>
                      </div>
                      {hasSpeakerSubscription && (
                        <span className="text-green-800 bg-green-100 p-1 rounded w-fit">
                          Speaker Subscriber
                        </span>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
